import { Alert as MuiAlert, Box, Grid } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { gql, useLazyQuery } from '@apollo/client'

// components
import BillViewSidebar from '../../../components/billing/billView/BillViewSidebar'
import BillViewHeader from '../../../components/billing/billView/BillViewHeader'
import BillPreview from '../../../components/billing/billDetail/BillPreview'

// others
import { useFetchPublicBillDetailsLazyQuery, FetchPublicBillDetailsQuery } from '../../../../generated'
import { BILL_VIEW_ROUTE, GRAPHQL_QUERY_POLICY } from '../../../constants'
import { Alert } from '../../../components/common/Alert'
import { calculateClientBillData } from '../../../utils/billing'
import useFetchBillInterest from '../../../hooks/useFetchBillInterest'
import useFetchOutstandingBills from '../../../hooks/useFetchOutstandingBills'

// Resolves the bill a client should be shown when the link they opened points at a voided bill.
// Declared inline rather than in the generated hooks so this stays a self-contained public query.
const FETCH_REPLACEMENT_BILL_TOKEN = gql`
  query FetchReplacementBillToken($token: String!) {
    fetchReplacementBillToken(token: $token) {
      token
      billId
    }
  }
`

const ClientBillPreview: FC = (): JSX.Element => {
  const [data, setData] = useState<FetchPublicBillDetailsQuery['fetchPublicBillDetails']>()
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null)

  const [fetchReplacementToken] = useLazyQuery(FETCH_REPLACEMENT_BILL_TOKEN, {
    fetchPolicy: 'network-only'
  })
  const { token } = useParams<{ token: string }>()
  const interestData = useFetchBillInterest({
    billId: data?.bill?.id,
    clientId: data?.client?.id,
    billType: data?.bill?.type,
    token
  })

  const outstandingBillsData = useFetchOutstandingBills({
    caseId: data?.bill?.case?.id,
    billId: data?.bill?.id,
    billStatus: data?.bill?.status,
    billType: data?.bill?.type,
    clientId: data?.client?.id,
    token
  })

  const navigate = useNavigate()

  const { bill, client, amountDue: billAmount } = data || {}

  const selectedAccessToken = bill?.accessTokens?.find(token => token.client?.id === client?.id)
  const clientWrittenOff =
    (selectedAccessToken?.writtenOffPrincipal || 0) + (selectedAccessToken?.writtenOffInterest || 0)
  const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

  const clientData = calculateClientBillData({
    paymentAllocations: bill?.paymentAllocations || [],
    previousBalance: outstandingBillsData?.previousBalance,
    bill: {
      amount: bill?.amount,
      splitBill: bill?.case?.billing?.splitBillEnabled,
      status: bill?.status,
      discount: bill?.discount,
      interestAccrued: interestData.interestAccrued,
      writtenOff: billWrittenOff
    },
    client: {
      id: client?.id,
      share: billAmount,
      writtenOff: clientWrittenOff
    }
  })

  const [fetchBillDetails, { refetch }] = useFetchPublicBillDetailsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError(error) {
      // A link for a bill that has since been voided must not bounce the client to the login
      // screen with "Invalid url". Try to send them to the matter's current bill instead — that's
      // the one the older balances were forwarded into, so it shows where they actually stand —
      // and fall back to explaining the situation if there is nothing to redirect to.
      if (error.message?.toLowerCase().includes('no longer available')) {
        const voidedMessage = error.message
        fetchReplacementToken({ variables: { token } })
          .then(({ data }) => {
            const replacementToken = data?.fetchReplacementBillToken?.token
            if (replacementToken && replacementToken !== token) {
              navigate(`${BILL_VIEW_ROUTE}/${replacementToken}`, { replace: true })
              return
            }
            setUnavailableMessage(voidedMessage)
          })
          .catch(() => setUnavailableMessage(voidedMessage))
        return
      }

      Alert.error('Invalid url')
      navigate('/')
    },

    onCompleted(data) {
      if (data?.fetchPublicBillDetails) {
        const { response } = data.fetchPublicBillDetails
        const { status } = response || {}

        if (status === 200 && data.fetchPublicBillDetails) {
          setData(data.fetchPublicBillDetails)
        }
      }
    }
  })

  const refetchData = () => {
    refetch()
    interestData.refetch()
    outstandingBillsData.refetch()
  }

  useEffect(() => {
    if (!token) {
      Alert.error('Invalid url')
      navigate('/')
      return
    }

    // Clear any stale voided-link state when the token changes (i.e. after a redirect).
    setUnavailableMessage(null)

    fetchBillDetails({
      variables: {
        fetchPublicBillDetailsInput: {
          token
        }
      }
    })
  }, [fetchBillDetails, navigate, token])

  if (unavailableMessage) {
    return (
      <Box>
        <BillViewHeader />

        <Box px={{ xs: 2, md: 6 }} py={4}>
          <MuiAlert severity="info">{unavailableMessage}</MuiAlert>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <BillViewHeader />

      <Grid container spacing={5} px={{ xs: 2, md: 6 }} py={4}>
        <Grid item xs={12} md={3}>
          {token && (
            <BillViewSidebar amount={clientData?.payableAmount || 0} token={token} bill={bill} refetch={refetchData} />
          )}
        </Grid>

        <Grid item xs={12} md={9}>
          <BillPreview
            bill={bill}
            client={client}
            selectedAccessToken={selectedAccessToken}
            clientData={clientData}
            interestData={interestData}
            outstandingBillsData={outstandingBillsData}
            isFinalBill
            isClientView
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default ClientBillPreview
